// 资源 CDN 域名真人测速 + 回传。设计 §2/§3/§8。
//
// user SPA「切换节点」测速:对 v_resource_node_public 暴露的资源域名顺序加载测试资源
// (/static/speedtest/{1..4}.webp,portal 同款文件集,见 server/internal/jumpdomain/
// resolve.go speedTestFiles)累加计时,并把结果 fire-and-forget 回传后端
// POST /gapi/public/speedtest-beacon(kind='resource')。
// 注意:单张 1.webp 仅用于**探活**(chain_health),测速必须 4 图累加。
//
// 2026-07-10:resource 也须过 PoW(防伪造刷桶自禁 DoS)。user SPA 载入 portal 同一
// js-challenge.min.js 全局脚本,用 window.JsChallenge.generateChallenge() 生成 PoW,
// 映射为后端 ChallengeInput 契约(meta 而非 fp)。JsChallenge 未载/生成失败 → 不回传。
//
// 探测资源 /static/speedtest/1.webp 由边缘补齐(设计 §8);缺失时测速 timeout,
// 回传仍走通(timed_out=true)—— 后端据此维护 health。

export type ResourceSpeedResult = { domain: string; ms: number; ok: boolean }

const TIMEOUT_MS = 20000
const TEST_PATH = '/static/speedtest/'
// portal 同款测速文件集(与后端 jumpdomain resolve.go speedTestFiles 对齐)
const PROBE_FILES = ['1.webp', '2.webp', '3.webp', '4.webp']

// js-challenge.min.js(全局脚本)暴露的 window.JsChallenge;generateChallenge 返回 fp,
// 需映射为后端期望的 meta(见 server/internal/jumpdomain/pow.go ChallengeInput)。
type JsChallengeGlobal = {
  generateChallenge(): {
    nonce: string
    issuedAt: number
    seed: string
    proof: number
    hash: string
    key: string
    fp: { ua: string; lang: string; tz: string; screen: string; webdriver: boolean; framed: boolean }
  }
}
declare global {
  interface Window {
    JsChallenge?: JsChallengeGlobal
  }
}

/**
 * 生成 beacon PoW(映射为后端 ChallengeInput 契约:{nonce,issuedAt,seed,proof,hash,key,meta})。
 * js-challenge 未载入或求解失败 → null(调用方据此不回传)。
 */
export function generateBeaconPoW(): unknown | null {
  const jc = typeof window !== 'undefined' ? window.JsChallenge : undefined
  if (!jc) return null
  try {
    const c = jc.generateChallenge()
    return {
      nonce: c.nonce,
      issuedAt: c.issuedAt,
      seed: c.seed,
      proof: c.proof,
      hash: c.hash,
      key: c.key,
      meta: {
        ua: c.fp.ua,
        lang: c.fp.lang,
        tz: c.fp.tz,
        screen: c.fp.screen,
        webdriver: c.fp.webdriver,
        framed: c.fp.framed,
      },
    }
  } catch {
    return null
  }
}

/**
 * fire-and-forget 回传测速结果。失败静默,不阻塞 UI。
 * pow:必须是 generateBeaconPoW() 的输出;为 null(js-challenge 未载)则**不回传**
 * (后端会因 PoW 缺失丢弃,发了也白发)。
 * timed_out = 未成功或 ms 非有限;ms 超时归零、成功取整。
 */
export function reportResourceSpeed(
  domain: string,
  ms: number,
  ok: boolean,
  pow: unknown | null,
): void {
  if (typeof fetch === 'undefined' || pow == null) return
  const timedOut = !ok || !isFinite(ms)
  const body = JSON.stringify({
    kind: 'resource',
    domain,
    ms: timedOut ? 0 : Math.round(ms),
    timed_out: timedOut,
    pow,
  })
  try {
    void fetch('/gapi/public/speedtest-beacon', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* ignore */
  }
}

// 用 Image 加载探测资源计时(跨域图片可加载,onerror 也能触发)。照 portal speedtest.ts::loadImage。
function loadProbe(url: string, timeoutMs: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const t0 = performance.now()
    const img = new Image()
    let done = false
    const timer = setTimeout(() => {
      if (done) return
      done = true
      img.src = ''
      reject(new Error('timeout'))
    }, timeoutMs)
    img.onload = () => {
      if (done) return
      done = true
      clearTimeout(timer)
      resolve(performance.now() - t0)
    }
    img.onerror = () => {
      if (done) return
      done = true
      clearTimeout(timer)
      reject(new Error('error'))
    }
    img.src = url + (url.includes('?') ? '&' : '?') + '_=' + Math.floor(t0)
  })
}

/**
 * 测单个资源域名:顺序加载 https://{domain}/static/speedtest/{1..4}.webp 累加耗时
 * (portal speedtest.ts::testDomain multi 模式同款)。
 * 全部 onload → ms=累加值;任一 onerror / 超时 → ok:false, ms:Infinity(短路)。
 */
export async function measureResourceDomain(
  domain: string,
  timeoutMs: number = TIMEOUT_MS,
): Promise<ResourceSpeedResult> {
  try {
    let total = 0
    for (const f of PROBE_FILES) {
      total += await loadProbe(`https://${domain}${TEST_PATH}${f}`, timeoutMs)
    }
    return { domain, ms: total, ok: true }
  } catch {
    return { domain, ms: Infinity, ok: false }
  }
}

/**
 * 并发测每个域名 + 各自 fire-and-forget 回传;返回结果供 UI 回填 latency。
 * 结果保序对应入参 domains(不排序;调用方按需自行处理)。
 */
export async function measureAndReportAll(domains: string[]): Promise<ResourceSpeedResult[]> {
  const pow = generateBeaconPoW() // 一次生成,本轮所有回传复用(PoW 无状态,freshness ±120s)
  return Promise.all(
    domains.map(async (domain) => {
      const r = await measureResourceDomain(domain)
      reportResourceSpeed(r.domain, r.ms, r.ok, pow)
      return r
    }),
  )
}
